import React from "react";
import { withTranslation } from "react-i18next";
import _ from "lodash";
import moment from "moment";
import cx from "classnames";
import submit from "submit-form";
import Chart from "../../parts/chart";
import DateInput from "../../parts/inputs/date-input";
import getDayRangeString from "../../utils/day-range-string";
import ToggleLinksInput from "../../parts/inputs/toggle-links-input";
import ShareModal from "../../parts/modals/share";
import VideoModal from "../../parts/modals/video";
import { rpeValue } from "../../utils/rpe-range";

const {
  WEIGHT_UNITS,
  DATE_FORMATS: { DATE_PICKER_FORMAT, DATE_DAY_FORMAT },
  PRIMARIES,
  ZONES,
} = require("../../constants");

const ZONES_LEGEND = _.map(ZONES, (name) => {
  return (
    <div key={name} className={`bar series ${name}`}>
      {_.capitalize(name)}
    </div>
  );
});

const PRIMARIES_LEGEND = _.map(PRIMARIES, (name) => {
  return (
    <div key={name} className={`bar series ${name}`}>
      {_.capitalize(name)}
    </div>
  );
});

class View extends React.Component {
  render() {
    const { t } = this.props;
    const { locals } = this.context;
    const {
      query,
      activeExercises,
      activeUser,
      exercisesVolume,
      exercisesVolumeGroupZones,
      exercisesVolumeMean,
      listCompetitions,
    } = locals;
    const { units = locals.units } = query;
    const baselineVolume = locals.fromMetric(
      _.get(exercisesVolumeMean, "total", 0),
      units
    );

    const activeDay = moment(query.day, DATE_PICKER_FORMAT);
    global.activeDay = activeDay;
    const selectDayRange = _.map(_.range(0, 7), (i) =>
      activeDay.clone().add(i, "days")
    );
    const groupedExercises = _.groupBy(
      _.sortBy(activeExercises, "date"),
      ({ date }) => moment(date).format(DATE_DAY_FORMAT)
    );
    const keys = _.range(0, 12);
    const labels = _.map(keys, (i) => `W${i + 1}`);

    const exercisesVolumeGroups = _.values(exercisesVolume);
    const totalVolumes = _.map(keys, (key) =>
      locals.fromMetric(_.sumBy(exercisesVolumeGroups, `${key}.volume`), units)
    );
    const volumeSeries = [
      {
        name: "total",
        className: "total",
        data: totalVolumes,
      },
    ];

    const baselineSeries = _.map(keys, () => baselineVolume);
    const maxVolume = _.max(totalVolumes.concat(baselineVolume));

    const selectedZones =
      query.zoneType === "total"
        ? exercisesVolumeGroupZones
        : _.pick(exercisesVolumeGroupZones, query.zoneType);
    const totalSelectedZoneVolume = locals.fromMetric(
      _(selectedZones).flatMap(_.values).sum(),
      units
    );
    const seriesZones = _(ZONES)
      .map((zone) => {
        return {
          name: zone,
          className: zone,
          value:
            locals.fromMetric(
              _.sumBy(_.values(selectedZones), zone) || 0,
              units
            ) / totalSelectedZoneVolume,
        };
      })
      .filter("value")
      .value();

    const nextCompetition = _(listCompetitions)
      .filter(({ date }) => moment(date).isSameOrAfter(moment().startOf("day")))
      .sortBy("date")
      .first();

    const daysToNextCompetition =
      nextCompetition &&
      moment(nextCompetition.date)
        .endOf("day")
        .diff(moment().startOf("day"), "days");

    return (
      <form id="dashboard-form" className="Dashboard" action="#_">
        <div className="Sub-Header">
          <h1 className="alt">{getDayRangeString(selectDayRange)}</h1>

          <div className="actions">
            <DateInput
              name="day"
              placeholder={t("homeWeek.datePlaceholder")}
              defaultValue={activeDay}
              onChange={handleDayChange}
            />
            <ToggleLinksInput
              name="period"
              options={[
                { text: t("homeWeek.daily"), value: "day" },
                { text: t("homeWeek.weekly"), value: "week" },
              ]}
            />
          </div>
        </div>

        <div className="Main">
          <div className="section welcome">
            <h4>
              {t("homeWeek.greeting", {
                name: _.get(activeUser, "name", "").split(/ +/)[0],
              })}
            </h4>
            {t("homeWeek.checkProgress")}
            {nextCompetition && daysToNextCompetition >= 0 ? (
              <div className="competition-tracker">
                <div className="text">{t("homeWeek.nextCompetition")}</div>
                {daysToNextCompetition === 0 ? (
                  <div className="days">
                    <b>{t("homeWeek.competitionToday")}</b>
                  </div>
                ) : daysToNextCompetition === 1 ? (
                  <div className="days">
                    <b>{t("homeWeek.competitionTomorrow")}</b>
                  </div>
                ) : (
                  <div className="days">
                    <b>{daysToNextCompetition}</b>{" "}
                    {t("homeWeek.competitionDays", {
                      count: daysToNextCompetition,
                    })}
                  </div>
                )}
                <a
                  href="/dashboard/competitions/"
                  className="text-button icon sm"
                >
                  <img src="/img/svg/icon_competition-2.svg" alt="icon" />
                  {t("homeWeek.competitionTracker")}
                </a>
              </div>
            ) : (
              <div className="competition-tracker">
                <a
                  href="/dashboard/competitions/"
                  className="text-button icon sm"
                >
                  <img src="/img/svg/icon_competition-2.svg" alt="icon" />
                  {t("homeWeek.competitionTracker")}
                </a>
              </div>
            )}
          </div>

          {!_.isEmpty(groupedExercises) ? (
            <div className="section">
              {query.expand ? (
                <a className="expand-all" href="/actions/refresh?expand=">
                  {t("homeWeek.collapseAll")}
                </a>
              ) : (
                <a className="expand-all" href="/actions/refresh?expand=true">
                  {t("homeWeek.expandAll")}
                </a>
              )}

              <h4 className="alt">{t("homeWeek.thisWeeksWorkout")}</h4>

              <div className="Dashboard-Weekly-Workouts">
                {_.map(groupedExercises, (exercises, day) => {
                  const date = moment(day);
                  const toggleDayId = `toggle-day-${day}`;
                  const seriesVolumeSplit = _(exercises)
                    .groupBy("primary")
                    .omit(["assistant"])
                    .map((exercises, name) => {
                      return {
                        name,
                        className: name,
                        value: locals.fromMetric(
                          _(exercises).flatMap("sets").sumBy("load"),
                          query.units
                        ),
                      };
                    })
                    .filter("value")
                    .value();
                  const groupTotalVolume = _.sumBy(seriesVolumeSplit, "value");

                  return (
                    <div key={day} className="Workout collapsible">
                      <input
                        key={query.expand}
                        type="checkbox"
                        id={toggleDayId}
                        defaultChecked={query.expand}
                      />
                      <div className="head">
                        <label
                          className="expand-toggle"
                          htmlFor={toggleDayId}
                        />
                        {date.format("dddd, MMMM D")}
                      </div>

                      <div className="details">
                        <div className="columns">
                          <div className="column" data-col="2/3">
                            {_.map(exercises, (exercise) => {
                              const isAssistant = exercise.type === "assistant";
                              const primary = !isAssistant
                                ? _.get(exercise, "primary")
                                : "Assistant";
                              const defaultRepMax = locals.fromMetric(
                                _.get(
                                  exercise,
                                  `repMaximums.${primary}`,
                                  _.get(activeUser, `repMaximums.${primary}`, 0)
                                )
                              );
                              const equipped =
                                !isAssistant &&
                                _.some(exercise.sets, "equipped");
                              const toggleExerciseId = `toggle-exercise-${exercise._id}`;
                              const collapsible = !_.isEmpty(exercise.sets);
                              const classname = !isAssistant
                                ? primary
                                : "assistant";

                              return (
                                <div
                                  key={exercise._id}
                                  className={cx("Exercise", classname, {
                                    equipped,
                                    collapsible,
                                  })}
                                >
                                  <input
                                    type="checkbox"
                                    id={toggleExerciseId}
                                  />
                                  <div className="head">
                                    <label
                                      className="expand-toggle"
                                      htmlFor={toggleExerciseId}
                                    />
                                    {_.capitalize(primary)}{" "}
                                    {exercise.secondary &&
                                      `(${_.capitalize(exercise.secondary)})`}
                                  </div>
                                  <div className="details">
                                    <div className="details-col-1">
                                      {_.map(exercise.sets, (it) => {
                                        const equippedRepMax =
                                          locals.fromMetric(it.equippedRepMax);
                                        const repMax =
                                          equippedRepMax || defaultRepMax;
                                        const load = locals.fromMetric(it.load);
                                        const percentRM = _.round(
                                          (load / repMax) * 100,
                                          1
                                        );
                                        const isEquipped =
                                          !isAssistant && it.equipped;
                                        const hasLoad = Boolean(load);
                                        return (
                                          <div className="routine">
                                            <div>
                                              <b>{t("homeWeek.sets")}</b>{" "}
                                              {it.sets}
                                            </div>
                                            <div>
                                              <b>{t("homeWeek.reps")}</b>{" "}
                                              {it.reps}
                                            </div>
                                            {hasLoad && (
                                              <div>
                                                <b>{t("homeWeek.load")}</b>{" "}
                                                {locals.withWeight(load)}
                                              </div>
                                            )}
                                            {hasLoad && !isAssistant && (
                                              <div>
                                                <b>{t("homeWeek.intensity")}</b>{" "}
                                                {percentRM}%
                                              </div>
                                            )}
                                            {it.rpe > 0 && (
                                              <div>
                                                <b>{t("homeWeek.rpe")}</b>{" "}
                                                {rpeValue(it.rpe, it.rpeLower)}
                                              </div>
                                            )}
                                            <div>
                                              <b>{t("homeWeek.zone")}</b>{" "}
                                              <span
                                                className={`zone-dot ${it.zone}`}
                                              />
                                            </div>

                                            {isEquipped && (
                                              <div>
                                                <b>{t("homeWeek.equipped")}</b>{" "}
                                                {it.equipment}
                                              </div>
                                            )}

                                            {isEquipped && (
                                              <div>
                                                <b>
                                                  {t("homeWeek.equippedRm")}
                                                </b>{" "}
                                                {locals.withWeight(
                                                  equippedRepMax
                                                )}
                                              </div>
                                            )}
                                          </div>
                                        );
                                      })}
                                    </div>
                                    <div className="details-col-2">
                                      {exercise.notes && (
                                        <div className="notes">
                                          <b>{t("homeWeek.notes")}</b>{" "}
                                          {exercise.notes}
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                          </div>

                          {!_.isEmpty(seriesVolumeSplit) && (
                            <div className="column" data-col="1/3">
                              <div
                                className="Chart-Frame pie-chart"
                                title={t("homeWeek.percentOfVolume")}
                              >
                                <div className="heading">
                                  {t("homeWeek.percentOfVolume")}
                                </div>

                                <div className="legend">{PRIMARIES_LEGEND}</div>

                                <div className="chart">
                                  <Chart
                                    type="Pie"
                                    data={{ series: seriesVolumeSplit }}
                                    options={{
                                      width: "160px",
                                      height: "160px",
                                      labelInterpolationFnc: (val) =>
                                        val &&
                                        (
                                          (val / groupTotalVolume) *
                                          100
                                        ).toFixed(0) + "%",
                                    }}
                                  />
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="foot">
                        <a
                          href={`/dashboard/program-builder/?day=${date.format(
                            DATE_DAY_FORMAT
                          )}`}
                          className="button outline"
                        >
                          {t("homeWeek.editWorkout")}
                        </a>
                        <a
                          className="share-workout text-button icon"
                          href={`/actions/refresh?modal[type]=share&modal[date]=${date.format(
                            DATE_DAY_FORMAT
                          )}}`}
                        >
                          <img src="/img/svg/icon_share.svg" alt="icon" />
                          {t("homeWeek.shareWorkout")}
                        </a>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ) : (
            <div className="section">
              <h4 className="alt">{t("homeWeek.thisWeeksWorkout")}</h4>
              <div className="Dashboard-Weekly-Workouts empty">
                <p>{t("homeWeek.noExercisesThisWeek")}</p>
                <a
                  className="text-button"
                  href={`/dashboard/program-builder/edit-exercise/?day=${activeDay.format(
                    DATE_DAY_FORMAT
                  )}`}
                >
                  {t("homeWeek.addExercise")}
                </a>
              </div>
            </div>
          )}

          <div className="section">
            <h4 className="alt">{t("homeWeek.baselineVolumeTitle")}</h4>
            <div className="Chart-Frame bar-chart">
              <div className="body">
                {!_.isEmpty(exercisesVolume) && (
                  <div>
                    <ToggleLinksInput
                      name="units"
                      options={[
                        { text: "kg", value: "metric" },
                        { text: "lbs", value: "imperial" },
                      ]}
                    />
                    <div className="legend">
                      <div className="bar series total">
                        {t("homeWeek.volume")}
                      </div>
                      <div className="line series-a">
                        {t("homeWeek.baselineVolume")}
                      </div>
                    </div>
                    <div className="axis-labels">
                      <div className="left">{t("homeWeek.volume")}</div>
                    </div>
                    <div className="chart-fullsize">
                      <div className="labels">
                        <Chart
                          type="Bar"
                          only="label"
                          data={{ series: volumeSeries }}
                          options={{
                            width: "60px",
                            height: "380px",
                            stackBars: true,
                            axisX: { showGrid: false, showLabel: false },
                            axisY: {
                              showGrid: false,
                              showLabel: true,
                              onlyInteger: true,
                              scaleMinSpace: 30,
                              low: 0,
                              high: maxVolume,
                            },
                          }}
                        />
                      </div>

                      <div className="data">
                        <Chart
                          type="Bar"
                          data={{
                            labels: labels,
                            series: volumeSeries,
                          }}
                          options={{
                            width: `${_.size(keys) * 40}px`,
                            height: "380px",
                            stackBars: true,
                            chartPadding: { left: 35, right: 75 },
                            axisX: { showGrid: false, showLabel: true },
                            axisY: {
                              showGrid: true,
                              showLabel: false,
                              scaleMinSpace: 30,
                              low: 0,
                              high: maxVolume,
                            },
                          }}
                          tooltip={(item, i) => {
                            const key = keys[i];
                            const date = moment(
                              query.day,
                              DATE_PICKER_FORMAT
                            ).subtract(11 - key, "weeks");
                            const dayRange = getDayRangeString(
                              [
                                date,
                                date.clone().add(1, "week").subtract(1, "day"),
                              ],
                              query
                            );
                            return (
                              <div className="tooltip" style={{ left: item.x }}>
                                <div className="details">
                                  <div>
                                    <b>{dayRange}</b>
                                  </div>
                                  <div>
                                    {t("homeWeek.volume")}{" "}
                                    <b>
                                      {locals.withWeight(
                                        totalVolumes[i],
                                        units
                                      )}
                                    </b>
                                  </div>
                                </div>
                              </div>
                            );
                          }}
                        />

                        {_.size(keys) >= 2 && (
                          <Chart
                            type="Line"
                            data={{ series: [baselineSeries] }}
                            options={{
                              height: "380px",
                              showPoint: false,
                              chartPadding: { left: 35, right: 75 },
                              axisX: { showGrid: false, showLabel: true },
                              axisY: {
                                low: 0,
                                high: maxVolume,
                                showGrid: false,
                                showLabel: false,
                                scaleMinSpace: 30,
                              },
                            }}
                          />
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="section">
            <h4 className="alt">{t("homeWeek.zoneIntensityTitle")}</h4>
            <div className="columns">
              <div className="column mobile-hidden" data-col="2/3">
                <div className="Chart-Frame" data-chart="relative-intensity">
                  <div className="body">
                    {!_.isEmpty(exercisesVolumeGroupZones) && (
                      <div>
                        <div className="bg">
                          <div />
                          <div />
                          <div />
                          <div />
                          <div />
                        </div>

                        <Chart
                          type="Line"
                          data={{ series: [_.reverse(seriesZones)] }}
                          options={{
                            fullWidth: true,
                            height: "380px",
                            showPoint: false,
                            lineSmooth:
                              process.browser &&
                              require("chartist").Interpolation.simple(),
                            axisX: { showGrid: false, showLabel: false },
                            axisY: {
                              low: 0,
                              high: 1,
                              stretch: true,
                              showGrid: false,
                              showLabel: false,
                              scaleMinSpace: 75,
                            },
                          }}
                        />

                        <div className="custom-labels">
                          <div className="x-labels">
                            <div>{t("homeWeek.percentOfVolume")}</div>
                            {_.reverse(
                              _.map(ZONES, (zone) => {
                                return (
                                  <div key={zone}>{_.capitalize(zone)}</div>
                                );
                              })
                            )}
                          </div>
                          <div className="y-labels">
                            {_.range(0, 110, 10)
                              .reverse()
                              .map((i) => {
                                return <div key={i}>{i}%</div>;
                              })}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="column" data-col="1/3">
                <div
                  className="Chart-Frame pie-chart"
                  data-chart="relative-intensity-2"
                >
                  <div className="head">
                    <div className="left-side">
                      <div className="group">
                        <ToggleLinksInput
                          className="tabs"
                          name="zoneType"
                          options={[
                            { text: "Total", value: "total" },
                            { text: "Squat", value: "squat" },
                            { text: "Bench", value: "bench" },
                            { text: "Deadlift", value: "deadlift" },
                          ]}
                        />
                      </div>
                    </div>
                  </div>

                  <div className="body">
                    {!_.isEmpty(exercisesVolume) && (
                      <div>
                        <div className="legend">{ZONES_LEGEND}</div>
                        <div className="chart">
                          <Chart
                            type="Pie"
                            data={{ series: seriesZones }}
                            options={{
                              labelInterpolationFnc: (val, i) => {
                                if (_.get(seriesZones, [i, "name"]) === "empty")
                                  return "";
                                return `${Math.round(val * 100)}%`;
                              },
                            }}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </form>
    );
  }
}

function handleDayChange() {
  submit("#_", { body: document.getElementById("dashboard-form") });
}

const HomeWeekView = withTranslation("dashboard")(View);

const HomeWeek = (app) => {
  app.get("/home", async ({ req, res, locals, load, api, fail }) => {
    locals.heading = ["Dashboard"];
    const { query } = req;
    const { units, activeUser } = locals;
    const user = locals.activeUser._id;
    const modal = _.get(query, "modal", {});

    _.defaults(query, {
      units,
      zoneType: "total",
      period: "week",
    });

    query.day = query.day && moment(query.day, DATE_PICKER_FORMAT);
    query.day = (
      query.day && query.day.isValid() ? query.day : moment()
    ).format(DATE_PICKER_FORMAT);
    const startDate = moment(query.day, DATE_PICKER_FORMAT).startOf("day");
    const endDate = moment(query.day, DATE_PICKER_FORMAT)
      .endOf("day")
      .add(6, "days");

    await Promise.all([
      load("activeExercises", {
        start: startDate,
        end: endDate,
      }),
      load("exercisesVolume", {
        period: "week",
        start: startDate.clone().subtract(11, "weeks"),
        end: startDate.clone().add(1, "week"),
      }),
      load("exercisesVolumeGroupZones", {
        start: startDate.clone().subtract(11, "weeks"),
        end: startDate.clone().add(1, "week"),
      }),
      load("exercisesVolumeMean", {
        period: "week",
        timeSpan: "6-months",
        day: moment(),
      }),
      load("listCompetitions"),
    ]);

    if (modal.type === "share" && !modal.snapshot) {
      // create share link
      try {
        const day = moment(modal.date || moment());
        const activeExercises = _.filter(locals.activeExercises, ({ date }) => {
          return (
            day.startOf("day").isSameOrBefore(date) &&
            day.endOf("day").isSameOrAfter(date)
          );
        });

        modal.snapshot = (
          await (
            await api("snapshot", {
              method: "POST",
              body: {
                type: "workout",
                data: JSON.stringify({
                  start: startDate.toJSON(),
                  end: endDate.toJSON(),
                  activeExercises,
                }),
              },
            })
          ).json()
        )._id;

        modal.date = getDayRangeString([day, day]);

        return res.refreshWith({ modal });
      } catch (e) {
        console.error(e.stack || e);
        fail(400, "Error creating share link.");
      }
    }

    // first login
    if (query.firstLogin && !activeUser.coach) {
      delete query.firstLogin;
      return res.redirect(`/dashboard/calendar?modal[type]=video`);
    }

    locals.modal = {
      share: <ShareModal />,
      video: <VideoModal />,
    }[modal.type];

    res.body = <HomeWeekView />;
  });
};

export default HomeWeek;
